import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Pending from '../components/Pending';
import SalesChart from '../components/SalesChart';
import PieChart from '../components/PieChart';
import LowStockInventory from '../components/LowStockInventory';
import { fetchClients, type ClientRow } from '../api/clients';
import { fetchProjectsCategorized, type ProjectRow } from '../api/projects';
import { fetchTasks, fetchAllTasks, type TaskRow } from '../api/tasks';
import { formatTaskDate } from '../utils/formatTaskDate';
import { formatDatePT } from '../utils/timezone';
import { fetchSalesProductCounts, fetchSiteActivity, type SalesProductCounts, type SiteActivityEntry } from '../api/orderApi';
import { useAuth } from '../context/AuthContext';
import { getRobotFleetStats } from '../data/inventory';
import {
  fetchDashboardSections,
  updateDashboardSections,
} from '../api/users';
import {
  DASHBOARD_SECTION_ORDER,
  DASHBOARD_FLOW_CARD_IDS,
  DASHBOARD_SECTION_LABELS,
  clearDashboardSectionsLegacy,
  loadDashboardSectionsLegacy,
  defaultDashboardSections,
  normalizeDashboardSections,
  countVisibleSections,
  type DashboardSectionId,
} from '../utils/dashboardSectionPrefs';
import '../components/Pending.css';
import './Dashboard.css';

const STATUS_FILTER_ORDER = ['To-Do', 'In Progress', 'On Hold'] as const;
type StatusFilter = typeof STATUS_FILTER_ORDER[number];

const TASKS_PANEL_TABS = [...STATUS_FILTER_ORDER, 'Projects'] as const;
type TasksPanelTab = (typeof TASKS_PANEL_TABS)[number];

const ACTIVITY_LOG_MAX_DISPLAY = 50;

const STATUS_PILL_COLORS: Record<string, string> = {
  Unassigned: '#8A8F93',
  'To-Do': '#bfdbfe',
  'In Progress': '#fde68a',
  'On Hold': '#fcd34d',
  Completed: '#bbf7d0',
};

const PRIORITY_PILL_COLORS: Record<string, string> = {
  Low: '#73BF4380',
  Medium: '#FFC10780',
  High: '#E48B5280',
  Urgent: '#ef444480',
};

const TAG_PILL_COLORS: Record<string, string> = {
  admin: '#fecaca',
  accounting: '#bbf7d0',
  sales: '#bfdbfe',
  marketing: '#fde68a',
  engineers: '#ddd6fe',
  installation: '#fed7aa',
  logistics: '#a5f3fc',
  corporate: '#fbcfe8',
  r_d: '#d1fae5',
  support: '#e0e7ff',
  customer_service: '#fef3c7',
  it: '#c7d2fe',
  operations: '#fcd5b0',
  finances: '#b8e0d2',
  manufacturing: '#e9d5ff',
  hr: '#fed7e2',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apiLeads, setApiLeads] = useState<ClientRow[]>([]);
  const [dashboardTasks, setDashboardTasks] = useState<TaskRow[]>([]);
  const [allTasks, setAllTasks] = useState<TaskRow[]>([]);
  const [salesData, setSalesData] = useState<SalesProductCounts | null>(null);
  const [siteActivity, setSiteActivity] = useState<SiteActivityEntry[]>([]);
  const [dashboardProjects, setDashboardProjects] = useState<ProjectRow[]>([]);
  const [tasksPanelTab, setTasksPanelTab] = useState<TasksPanelTab>('To-Do');
  const [dashboardSections, setDashboardSections] = useState(defaultDashboardSections);
  const [dashboardSectionsReady, setDashboardSectionsReady] = useState(false);
  const [dashboardSectionsSaveError, setDashboardSectionsSaveError] = useState('');
  const [dashboardLayoutOpen, setDashboardLayoutOpen] = useState(false);
  const layoutSettingsRef = useRef<HTMLDivElement>(null);
  const lastSyncedSectionsJson = useRef(JSON.stringify(defaultDashboardSections()));
  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('admin');

  const showDash = (id: DashboardSectionId) => dashboardSections[id];
  const dashboardFlowCardsVisible = DASHBOARD_FLOW_CARD_IDS.some((id) => showDash(id));

  useEffect(() => {
    if (!user?.id) {
      setDashboardSectionsReady(false);
      setDashboardSections(defaultDashboardSections());
      setDashboardSectionsSaveError('');
      return;
    }
    let cancelled = false;
    setDashboardSectionsReady(false);
    setDashboardSectionsSaveError('');
    void (async () => {
      try {
        const raw = await fetchDashboardSections(user.id);
        if (cancelled) return;
        const normalized = normalizeDashboardSections(raw);
        const s = JSON.stringify(normalized);
        lastSyncedSectionsJson.current = s;
        setDashboardSections(normalized);
        setDashboardSectionsReady(true);
        clearDashboardSectionsLegacy(user.id);
      } catch {
        if (cancelled) return;
        const legacy = loadDashboardSectionsLegacy(user.id);
        const normalized = legacy ?? defaultDashboardSections();
        const s = JSON.stringify(normalized);
        lastSyncedSectionsJson.current = s;
        setDashboardSections(normalized);
        setDashboardSectionsReady(true);
        setDashboardSectionsSaveError(
          'Could not load layout from the server. Using defaults or data saved on this device only.'
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !dashboardSectionsReady) return;
    const serialized = JSON.stringify(dashboardSections);
    if (serialized === lastSyncedSectionsJson.current) return;
    const snapshot = dashboardSections;
    const uid = user.id;
    const timer = window.setTimeout(() => {
      void updateDashboardSections(uid, snapshot)
        .then((serverSections) => {
          setDashboardSections((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(snapshot)) return prev;
            const next = normalizeDashboardSections(serverSections);
            lastSyncedSectionsJson.current = JSON.stringify(next);
            return next;
          });
          setDashboardSectionsSaveError('');
          clearDashboardSectionsLegacy(uid);
        })
        .catch((err: unknown) => {
          setDashboardSections((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(snapshot)) return prev;
            setDashboardSectionsSaveError(
              err instanceof Error ? err.message : 'Could not save layout. Check your connection and try again.'
            );
            return prev;
          });
        });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [dashboardSections, user?.id, dashboardSectionsReady]);

  useEffect(() => {
    if (!dashboardLayoutOpen) return;
    const onDown = (e: MouseEvent) => {
      if (layoutSettingsRef.current && !layoutSettingsRef.current.contains(e.target as Node)) {
        setDashboardLayoutOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [dashboardLayoutOpen]);

  const toggleDashboardSection = (id: DashboardSectionId) => {
    setDashboardSections((prev) => {
      if (!prev[id]) {
        return { ...prev, [id]: true };
      }
      if (countVisibleSections(prev) <= 1) {
        return prev;
      }
      return { ...prev, [id]: false };
    });
  };

  useEffect(() => {
    let cancelled = false;
    fetchClients('lead')
      .then((leads) => {
        if (!cancelled) setApiLeads(leads);
      })
      .catch(() => {
        if (!cancelled) setApiLeads([]);
      });
    return () => { cancelled = true; };
  }, []);

  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleLeadClick = (leadId: number) => {
    navigate(`/lead/${leadId}`);
  };

  const handleProjectClick = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  useEffect(() => {
    if (!user?.id) {
      setDashboardTasks([]);
      return;
    }
    let cancelled = false;
    fetchTasks(user.id)
      .then((sections) => {
        if (!cancelled) {
          setDashboardTasks([...sections.todo, ...sections.inProgress, ...(sections.onHold ?? [])]);
        }
      })
      .catch(() => {
        if (!cancelled) setDashboardTasks([]);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setDashboardProjects([]);
      return;
    }
    let cancelled = false;
    fetchProjectsCategorized(user.id)
      .then((res) => {
        if (!cancelled) setDashboardProjects(res.my ?? []);
      })
      .catch(() => {
        if (!cancelled) setDashboardProjects([]);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    fetchSalesProductCounts()
      .then((data) => {
        if (!cancelled) setSalesData(data);
      })
      .catch(() => {
        if (!cancelled) setSalesData(null);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSiteActivity(50)
      .then((entries) => {
        if (!cancelled) setSiteActivity(entries);
      })
      .catch(() => {
        if (!cancelled) setSiteActivity([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isAdmin || !user?.id) {
      setAllTasks([]);
      return;
    }
    let cancelled = false;
    fetchAllTasks(user.id)
      .then((list) => {
        if (!cancelled) setAllTasks(list);
      })
      .catch(() => {
        if (!cancelled) setAllTasks([]);
      });
    return () => { cancelled = true; };
  }, [isAdmin, user?.id]);

  useEffect(() => {
    const cards = document.querySelectorAll<HTMLElement>('.dashboard-card');
    const cleanups: (() => void)[] = [];
    cards.forEach((card) => {
      const handler = (e: MouseEvent) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        card.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
      };
      card.addEventListener('mousemove', handler);
      cleanups.push(() => card.removeEventListener('mousemove', handler));
    });
    return () => cleanups.forEach((fn) => fn());
  }, [dashboardSections]);

  // Sales chart data from completed orders (TIM-E Bot, BIM-E), or fallback zeros
  const defaultSalesPeriod = {
    productData: [
      { product: 'TIM-E Bot', sales: 0 },
      { product: 'BIM-E bot', sales: 0 },
    ],
    totalData: [{ date: formatDatePT(new Date(), { month: 'short', year: 'numeric', day: undefined }, ''), total: 0 }],
  };
  const salesProductData = salesData
    ? {
        '1month': salesData['1month'].productData,
        '3months': salesData['3months'].productData,
        '6months': salesData['6months'].productData,
        '1year': salesData['1year'].productData,
      }
    : {
        '1month': defaultSalesPeriod.productData,
        '3months': defaultSalesPeriod.productData,
        '6months': defaultSalesPeriod.productData,
        '1year': defaultSalesPeriod.productData,
      };
  const salesTotalData = salesData
    ? {
        '1month': salesData['1month'].totalData,
        '3months': salesData['3months'].totalData,
        '6months': salesData['6months'].totalData,
        '1year': salesData['1year'].totalData,
      }
    : {
        '1month': defaultSalesPeriod.totalData,
        '3months': defaultSalesPeriod.totalData,
        '6months': defaultSalesPeriod.totalData,
        '1year': defaultSalesPeriod.totalData,
      };
  
  const robotStats = getRobotFleetStats();
  const robotsData = [
    {
      label: 'Deployed',
      value: robotStats.deployed,
      color: '#73BF43'
    },
    {
      label: 'In Storage',
      value: robotStats.inStorage,
      color: '#8A8F93'
    },
    {
      label: 'In Need of Maintenance',
      value: robotStats.needsMaintenance,
      color: '#E48B52'
    }
  ];

  const taskCountByStatus = STATUS_FILTER_ORDER.reduce<Record<string, number>>((acc, s) => {
    acc[s] = dashboardTasks.filter((t) => t.status === s).length;
    return acc;
  }, {});

  const filteredTasks = dashboardTasks.filter((t) => t.status === tasksPanelTab);

  /** Absolute date + time in the viewer's local timezone and locale (same idea as task activity). */
  const formatActivityTime = (createdAt: string) => {
    try {
      const d = new Date(createdAt);
      if (Number.isNaN(d.getTime())) return createdAt;
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return createdAt;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-layout">
        <Sidebar />
        <main className="dashboard-main">

          <div>
            <PageHeader
              title={`Hello, ${user?.username ?? 'there'}!`}
              subtitle="Welcome to TechForce Robotics Company Portal"
              action={
                user?.id ? (
                  <div className="dashboard-layout-settings-wrap" ref={layoutSettingsRef}>
                    <button
                      type="button"
                      className="dashboard-layout-settings-btn"
                      aria-expanded={dashboardLayoutOpen}
                      aria-haspopup="true"
                      aria-label="Customize dashboard sections"
                      title="Customize dashboard"
                      onClick={() => setDashboardLayoutOpen((o) => !o)}
                    >
                      <FontAwesomeIcon icon={faGear} />
                    </button>
                    {dashboardLayoutOpen ? (
                      <div
                        className="dashboard-layout-settings-panel"
                        role="dialog"
                        aria-label="Dashboard sections"
                      >
                        <p className="dashboard-layout-settings-title">Dashboard sections</p>
                        <p className="dashboard-layout-settings-hint">
                          Show or hide cards on your dashboard. Your choices are saved to your account and sync across
                          devices.
                        </p>
                        {dashboardSectionsSaveError ? (
                          <p className="dashboard-layout-settings-error" role="alert">
                            {dashboardSectionsSaveError}
                          </p>
                        ) : null}
                        {DASHBOARD_SECTION_ORDER.map((id) => {
                          const checked = dashboardSections[id];
                          const onlyVisible = countVisibleSections(dashboardSections) === 1 && checked;
                          return (
                            <label key={id} className="dashboard-layout-settings-row">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={onlyVisible}
                                onChange={() => toggleDashboardSection(id)}
                              />
                              <span>{DASHBOARD_SECTION_LABELS[id]}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null
              }
            />
          </div>

          <div className="dashboard-content">
            {/* Cards in fixed order; flex-wrap fills each row with up to 3 cards (any mix). */}
            {dashboardFlowCardsVisible ? (
              <div className="dashboard-grid dashboard-cards-flow">
                {DASHBOARD_FLOW_CARD_IDS.map((sectionId) => {
                  if (!showDash(sectionId)) return null;
                  switch (sectionId) {
                    case 'pending':
                      return (
                        <div key={sectionId} className="dash-pending dashboard-card">
                          <Pending />
                        </div>
                      );
                    case 'tasks':
                      return (
                        <div key={sectionId} className="dash-tasks dashboard-card">
                          <h3 className="card-title" style={{ cursor: 'pointer' }} onClick={() => navigate('/tasks')}>
                            Your Tasks
                          </h3>
                          <div className="pending-tabs">
                            {TASKS_PANEL_TABS.map((tab) => {
                              const count =
                                tab === 'Projects'
                                  ? dashboardProjects.length
                                  : taskCountByStatus[tab as StatusFilter];
                              return (
                                <button
                                  key={tab}
                                  type="button"
                                  className={`pending-tab ${tasksPanelTab === tab ? 'active' : ''}`}
                                  onClick={() => setTasksPanelTab(tab)}
                                >
                                  <span className="pending-tab-label">{tab}</span>
                                  <span className="pending-count">{count}</span>
                                </button>
                              );
                            })}
                          </div>
                          <div className="card-content">
                            {tasksPanelTab === 'Projects' ? (
                              dashboardProjects.length === 0 ? (
                                <p className="page-subtitle" style={{ margin: 0 }}>
                                  No projects yet. You&apos;ll see projects here when you create one or are added as a
                                  collaborator.
                                </p>
                              ) : (
                                dashboardProjects.map((p) => {
                                  const isOwner = user?.id != null && p.created_by_user_id === user.id;
                                  const dateBits = [
                                    formatTaskDate(p.start_date),
                                    formatTaskDate(p.expected_end_date),
                                  ].filter((x) => x && x !== '—');
                                  return (
                                    <div key={p.id} className="dash-list-item" onClick={() => handleProjectClick(p.id)}>
                                      <span className="dash-list-item-primary">{p.name}</span>
                                      <span className="dash-list-item-secondary">
                                        {isOwner ? 'Owner' : 'Collaborator'}
                                        {p.status === 'On Hold' ? ' · On Hold' : ''}
                                        {dateBits.length > 0 ? ` · ${dateBits.join(' → ')}` : ''}
                                      </span>
                                    </div>
                                  );
                                })
                              )
                            ) : filteredTasks.length === 0 ? (
                              <p className="page-subtitle" style={{ margin: 0 }}>
                                No {tasksPanelTab.toLowerCase()} tasks
                              </p>
                            ) : (
                              filteredTasks.map((task) => (
                                <div key={task.id} className="dash-list-item" onClick={() => handleTaskClick(task.id)}>
                                  <span className="dash-list-item-primary">{task.name}</span>
                                  {task.priority && (
                                    <div className="dashboard-task-pills">
                                      <span
                                        className="dashboard-pill"
                                        style={{
                                          backgroundColor: PRIORITY_PILL_COLORS[task.priority] ?? '#e5e7eb',
                                        }}
                                      >
                                        {task.priority}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    case 'leads':
                      return (
                        <div key={sectionId} className="dash-leads dashboard-card">
                          <h3 className="card-title" style={{ cursor: 'pointer' }} onClick={() => navigate('/client')}>
                            Leads
                          </h3>
                          <div className="card-content">
                            {apiLeads.length === 0 ? (
                              <p className="page-subtitle" style={{ margin: 0 }}>
                                No leads
                              </p>
                            ) : (
                              apiLeads.map((lead) => (
                                <div key={lead.id} className="dash-list-item" onClick={() => handleLeadClick(lead.id)}>
                                  <span className="dash-list-item-primary">{lead.company}</span>
                                  <span className="dash-list-item-secondary">{lead.point_of_contact}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    case 'sales':
                      return (
                        <div key={sectionId} className="dash-sales dashboard-card dashboard-chart-card">
                          <SalesChart productData={salesProductData} totalData={salesTotalData} />
                        </div>
                      );
                    case 'inventory':
                      return (
                        <div key={sectionId} className="dash-inventory">
                          <LowStockInventory />
                        </div>
                      );
                    case 'robots':
                      return (
                        <div key={sectionId} className="dash-robots dashboard-card dashboard-chart-card">
                          <h3 className="chart-title">Robots Online</h3>
                          <div className="robots-chart-wrapper">
                            <PieChart data={robotsData} size={150} onSliceClick={() => navigate('/inventory')} />
                          </div>
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              </div>
            ) : null}

            {/* Activity Log — full width below, not minimized */}
            {showDash('activity') ? (
            <div className="dash-activity dashboard-card">
              <h3 className="activity-log-title">Activity Log</h3>
              <div className="activity-log-content">
                <div className="activity-log-list">
                  {siteActivity.length === 0 ? (
                    <p className="activity-log-empty">No activity recorded yet.</p>
                  ) : (
                    siteActivity.slice(0, ACTIVITY_LOG_MAX_DISPLAY).map((entry) => (
                      <div key={entry.id} className="activity-log-item">
                        <span className="activity-log-time">[{formatActivityTime(entry.created_at)}]</span>{' '}
                        {entry.action}
                        {entry.user && entry.user !== 'System' && (
                          <span className="activity-log-user"> — {entry.user}</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;


